import { IOnSyncEvent, OnSyncEventType } from '../../api/events.dao';
import { MeetingDetailsUpdatedChangeset } from './meeting-changeset/meeting-details-updated.changeset';
import { MeetingSyncData } from './index';
import { OperatorsAddedToTheMeeting } from './meeting-changeset/operators-added-to-the-meeting.changeset';
import { OperatorsRemovedFromTheMeeting } from './meeting-changeset/operators-removed-from-the-meeting.changeset';
import { OperatorsUpdatedInTheMeeting } from './meeting-changeset/operators-updated-in-the-meeting.changeset';
import { OperatorsAddedToTheMeetingRoom } from './meeting-changeset/operators-added-to-the-meeting-room.changeset';
import { OperatorsRemovedFromTheMeetingRoom } from './meeting-changeset/operators-removed-from-the-meeting-room.changeset';
import { OperatorsUpdatedInTheMeetingRoom } from './meeting-changeset/operators-updated-in-the-meeting-room.changeset';
import { AttendeeUpdatedChangeset } from './meeting-changeset/attendee-updated.changeset';
import { AttendeeUpdatedInTheMeetingChangeset } from './meeting-changeset/attendee-updated-in-the-meeting.changeset';

export type EventDataSyncChangeset = AttendeeUpdatedChangeset;

export type MeetingDataSyncChangeset =
    AttendeeUpdatedInTheMeetingChangeset
    | MeetingDetailsUpdatedChangeset
    | OperatorsAddedToTheMeeting
    | OperatorsUpdatedInTheMeeting
;

export type MeetingRoomDataSyncChangeset =
    OperatorsAddedToTheMeetingRoom
    | OperatorsRemovedFromTheMeetingRoom
    | OperatorsUpdatedInTheMeetingRoom
;

type SyncDataChangeset = EventDataSyncChangeset | MeetingDataSyncChangeset | MeetingRoomDataSyncChangeset;

export class MeetingChangesetMapper {
    static syncedEventDataToMeetingChangeset(syncEventData: IOnSyncEvent): SyncDataChangeset {
        const syncData = this.subscriptionResponseDataToMeetingSyncData(syncEventData);

        return this.constructChangeset(syncData);
    }

    public static isMeetingSyncEvent(type: OnSyncEventType): boolean {
        return this.meetingSyncEventTypes.includes(type);
    }
    public static isMeetingRoomSyncEvent(type: OnSyncEventType): boolean {
        return this.meetingRoomSyncEventTypes.includes(type);
    }
    public static isEventSyncEvent(type: OnSyncEventType): boolean {
        return this.eventSyncEventTypes.includes(type);
    }

    private static subscriptionResponseDataToMeetingSyncData(responseData: IOnSyncEvent): MeetingSyncData {
        try {
            return {
                ...responseData,
                body: JSON.parse(responseData.body)
            };
        } catch (e) {
            throw new Error(`Failed to parse subscription response data: ${e.message}`);
        }
    }

    private static constructChangeset(meetingSyncData: MeetingSyncData): SyncDataChangeset {
        let Constructor;

        switch (meetingSyncData.type) {
            case OnSyncEventType.ATTENDEE_UPDATED:
                Constructor = AttendeeUpdatedChangeset;
                break;
            case OnSyncEventType.MEETING_ATTENDEE_UPDATED:
                Constructor = AttendeeUpdatedInTheMeetingChangeset;
                break;
            case OnSyncEventType.MEETING_DETAILS_UPDATED:
                Constructor = MeetingDetailsUpdatedChangeset;
                break;
            case OnSyncEventType.MEETING_OPERATOR_ADDED:
                Constructor = OperatorsAddedToTheMeeting;
                break;
            case OnSyncEventType.MEETING_OPERATOR_REMOVED:
                Constructor = OperatorsRemovedFromTheMeeting;
                break;
            case OnSyncEventType.MEETING_OPERATOR_UPDATED:
                Constructor = OperatorsUpdatedInTheMeeting;
                break;
            case OnSyncEventType.ROOM_OPERATOR_ADDED:
                Constructor = OperatorsAddedToTheMeetingRoom;
                break;
            case OnSyncEventType.ROOM_OPERATOR_REMOVED:
                Constructor = OperatorsRemovedFromTheMeetingRoom;
                break;
            case OnSyncEventType.ROOM_OPERATOR_UPDATED:
                Constructor = OperatorsUpdatedInTheMeetingRoom;
                break;
        }

        if (Constructor) return new Constructor(meetingSyncData);
    }

    private static get meetingSyncEventTypes(): OnSyncEventType[] {
        return [
            OnSyncEventType.MEETING_DETAILS_UPDATED,
            OnSyncEventType.MEETING_OPERATOR_ADDED,
            OnSyncEventType.MEETING_OPERATOR_REMOVED,
            OnSyncEventType.MEETING_OPERATOR_UPDATED,
            OnSyncEventType.MEETING_ATTENDEE_UPDATED
        ];
    }
    private static get meetingRoomSyncEventTypes(): OnSyncEventType[] {
        return [
            OnSyncEventType.ROOM_OPERATOR_ADDED,
            OnSyncEventType.ROOM_OPERATOR_REMOVED,
            OnSyncEventType.ROOM_OPERATOR_UPDATED
        ];
    }
    private static get eventSyncEventTypes(): OnSyncEventType[] {
        return [
            OnSyncEventType.ATTENDEE_UPDATED
        ];
    }
}
